import React, { useState } from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { ShieldCheck, Shield, ShieldOff, UserPlus, Check, Ban, Copy, Loader2, KeyRound, Users, AlertTriangle, Pencil, Mail, MailX, Phone } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/lib/AuthContext';
import { toast } from 'sonner';
import RepProfileFields, { EMPTY_PROFILE, getMissingProfileFields } from '@/components/RepProfileFields';
import { FIELD_LABELS } from '@/components/ProfileCompletenessGate';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import PageContainer from '@/components/PageContainer';
import { formatPhoneAsTyped } from '@/lib/phone';

/**
 * User administration.
 *
 * Rebuilt for the new API. Base44 handed out invitation links that the
 * recipient redeemed by registering themselves, backed by an Invitation table
 * and a UserApproval table. Both had zero rows in production and both are cut
 * (CLAUDE.md section 2). An admin creates the account directly now, and
 * TradeSync mails the rep a link to set their own password, from the
 * admin's own Gmail, so the admin never sees or hands over a credential
 * the rep goes on using. "New password" (below) is unrelated: that resets
 * an already active account's password, for someone locked out later.
 *
 * Deleting a user is deliberately absent. Contacts record who captured them,
 * so removing a rep would erase who brought in 106 leads. Suspending is what
 * the old "remove" button should have been.
 */

const statusStyles = {
  active: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  suspended: 'bg-orange-100 text-orange-700 border-orange-200',
  invited: 'bg-sky-100 text-sky-700 border-sky-200',
};

export default function Admin() {
  const { user, isAdmin } = useAuth();
  const queryClient = useQueryClient();

  // Task: split user Name into First/Last, 2026-09-20.
  const [newUser, setNewUser] = useState({ email: '', first_name: '', last_name: '' });
  const [newProfile, setNewProfile] = useState(EMPTY_PROFILE);
  // Task: admin-only "Shared Account" checkbox, 2026-09-16. Only ever read
  // from this create form, never editable anywhere a regular rep can
  // reach: it is local state here, not a RepProfileFields field, so it
  // cannot leak into Profile.jsx or EditRep.jsx by sharing a component.
  const [isDeviceAccount, setIsDeviceAccount] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);

  const [creating, setCreating] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);

  /**
   * The one time password from a create or a reset. Held in state and shown
   * once, because the server hashes it and genuinely cannot show it again.
   */
  const [issued, setIssued] = useState(null);

  // Which required fields Create or Save profile found missing, shown in a
  // modal by label, same pattern as Shows.jsx: the alternative is a Save
  // button that silently does nothing with no indication why.
  const [missingFields, setMissingFields] = useState([]);

  // Mandatory bulk reassignment, task: contact reassignment (2026-09-08).
  // Set when the API refuses a suspend because the rep still owns contacts;
  // null the rest of the time. { target, contactCount, to }
  const [reassignDialog, setReassignDialog] = useState(null);
  const [reassigning, setReassigning] = useState(false);

  const { data: users = [], isLoading } = useQuery({
    queryKey: ['admin-users'],
    queryFn: () => base44.entities.User.list(),
    enabled: isAdmin,
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['admin-users'] });

  // ZoomInfo phone backfill, task: "Re-run ZoomInfo" admin tool (2026-09-24).
  // Candidates are read on mount, zero credit cost, so the button's own
  // confirmation dialog always has a real, current count and name list
  // rather than a stale one from whenever this page last loaded.
  const { data: backfillData, refetch: refetchBackfillCandidates } = useQuery({
    queryKey: ['zoominfo-phone-backfill-candidates'],
    queryFn: () => base44.zoominfo.phoneBackfillCandidates(),
    enabled: isAdmin,
  });
  const [backfillBusy, setBackfillBusy] = useState(false);
  const [backfillResult, setBackfillResult] = useState(null);

  const handleRunBackfill = async () => {
    const count = backfillData?.count ?? 0;
    if (count === 0) {
      toast.info('No contacts need a ZoomInfo phone backfill right now.');
      return;
    }
    const names = backfillData.candidates.map((c) => c.name).join(', ');
    const confirmed = window.confirm(
      `Re-run ZoomInfo for ${count} contact${count === 1 ? '' : 's'}: ${names}. ` +
        `This spends one real ZoomInfo credit per contact. Continue?`
    );
    if (!confirmed) return;

    setBackfillBusy(true);
    setBackfillResult(null);
    try {
      const result = await base44.zoominfo.runPhoneBackfill();
      setBackfillResult(result);
      if (!result.ran) {
        toast.error(result.reason || 'ZoomInfo backfill did not run.');
      } else {
        const found = result.outcomes.filter((o) => o.outcome === 'phone_found').length;
        toast.success(
          `Ran ${result.processed} lookup${result.processed === 1 ? '' : 's'}, ${found} phone number${found === 1 ? '' : 's'} found.`
        );
      }
      refetchBackfillCandidates();
    } catch (err) {
      toast.error(err.message || 'ZoomInfo backfill failed.');
    } finally {
      setBackfillBusy(false);
    }
  };

  // Task: confirm 'mobilePhone' before it ever reaches a live contact
  // (2026-09-24). One isolated call, never touches real contact data or the
  // shared enrichment path either way; see worker/lib/zoominfo.ts's
  // testMobilePhoneField for exactly what this proves.
  const [mobilePhoneTestBusy, setMobilePhoneTestBusy] = useState(false);
  const [mobilePhoneTestResult, setMobilePhoneTestResult] = useState(null);

  const handleTestMobilePhoneField = async () => {
    const confirmed = window.confirm(
      "Make one real ZoomInfo call requesting 'mobilePhone' against Henry Schuck (ZoomInfo's own CEO, never a real prospect)? Spends one real credit."
    );
    if (!confirmed) return;

    setMobilePhoneTestBusy(true);
    setMobilePhoneTestResult(null);
    try {
      const result = await base44.zoominfo.testMobilePhoneField();
      setMobilePhoneTestResult(result);
      toast[result.ok ? 'success' : 'error'](
        result.ok ? `ZoomInfo accepted 'mobilePhone' (status ${result.status}).` : `ZoomInfo rejected it (status ${result.status}).`
      );
    } catch (err) {
      toast.error(err.message || 'Could not reach ZoomInfo.');
    } finally {
      setMobilePhoneTestBusy(false);
    }
  };

  const copy = (text) => {
    navigator.clipboard?.writeText(text);
    toast.success('Copied');
  };

  const handleCreate = async (e) => {
    e.preventDefault();

    const missing = [
      ...(newUser.first_name.trim() ? [] : [{ key: 'first_name', label: 'First name' }]),
      ...(newUser.email.trim() ? [] : [{ key: 'email', label: 'Email' }]),
      // None of RepProfileFields' own required fields (cell_phone) apply to
      // a shared device account, the same reasoning worker/routes/users.ts
      // and worker/lib/userProfile.ts apply server side.
      ...(isDeviceAccount ? [] : getMissingProfileFields(newProfile)),
    ];
    if (missing.length > 0) {
      setMissingFields(missing);
      return;
    }

    setCreating(true);
    try {
      const created = await base44.entities.User.create({
        email: newUser.email.trim().toLowerCase(),
        first_name: newUser.first_name.trim(),
        last_name: newUser.last_name.trim(),
        is_device_account: isDeviceAccount,
        // The personal fields never rendered while the checkbox is on, so
        // this is always whatever EMPTY_PROFILE already is in that case:
        // sent anyway, harmlessly, rather than special cased away, since
        // the server already ignores/skips exactly the parts that would
        // otherwise matter (cell_phone, the vCard slug auto-generation).
        ...newProfile,
      });
      setNewUser({ email: '', first_name: '', last_name: '' });
      setNewProfile(EMPTY_PROFILE);
      setIsDeviceAccount(false);
      setAddDialogOpen(false);
      refresh();
      // The account itself is never lost over a Google hiccup, so a failed
      // invite send is a warning, not a thrown error: the account exists,
      // it just needs Resend invite once the problem is fixed.
      if (created.warning) toast.warning(created.warning);
      else toast.success(`Invite sent to ${created.email}`);
    } catch (err) {
      toast.error(err.message || 'Could not create that user');
    } finally {
      setCreating(false);
    }
  };

  const [resendingId, setResendingId] = useState(null);

  const resendInvite = async (u) => {
    setResendingId(u.id);
    try {
      await base44.entities.User.resendInvite(u.id);
      toast.success(`Invite resent to ${u.email}`);
    } catch (err) {
      toast.error(err.message || 'Could not resend the invite');
    } finally {
      setResendingId(null);
    }
  };

  const act = async (action, target, extra = {}) => {
    setActionLoading(target.id);
    try {
      if (action === 'reset') {
        const result = await base44.entities.User.resetPassword(target.id);
        setIssued({
          email: target.email,
          password: result.temporary_password,
          label: 'New password issued',
        });
        if (result.warning) toast.warning(result.warning);
        else toast.success('Password reset, and their other sessions were ended');
      } else {
        const result = await base44.functions.invoke('manageUser', { action, userId: target.id, ...extra });
        toast.success(
          action === 'suspend'
            ? (result.reassigned_contact_count > 0
                ? `User suspended. ${result.reassigned_contact_count} contact${result.reassigned_contact_count === 1 ? '' : 's'} reassigned.`
                : 'User suspended')
            : action === 'reactivate' ? 'User reactivated'
            : action === 'promote' ? 'Promoted to admin'
            : 'Admin removed',
        );
      }
      setReassignDialog(null);
      refresh();
    } catch (err) {
      // The API refused the suspend because this rep still owns contacts.
      // Mandatory, not optional (task: contact reassignment, 2026-09-08):
      // prompt for who to move them to rather than just reporting the error.
      if (action === 'suspend' && err.error === 'reassignment_required') {
        setReassignDialog({ target, contactCount: err.data?.contact_count || 0, to: '' });
        return;
      }
      toast.error(err.message || 'That did not work');
    } finally {
      setActionLoading(null);
    }
  };

  const confirmReassignAndSuspend = () => {
    if (!reassignDialog?.to) return;
    setReassigning(true);
    act('suspend', reassignDialog.target, { reassign_contacts_to: reassignDialog.to }).finally(() => setReassigning(false));
  };

  if (!isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-4">
        <div className="text-center">
          <ShieldCheck className="w-12 h-12 text-muted-foreground/40 mx-auto mb-3" />
          <h1 className="text-xl font-bold text-foreground mb-1">Admins only</h1>
          <p className="text-sm text-muted-foreground mb-4">You don't have access to this page.</p>
          <Link to="/"><Button variant="outline">Back to app</Button></Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background safe-top pb-24">
      <PageContainer size="lg" className="px-4 py-6">
        <div className="flex items-center gap-3 mb-6">
          <Link to="/"><Button variant="outline" className="min-h-9 px-4 text-sm">Back</Button></Link>
          <div className="flex-1">
            <h1 className="text-xl font-bold text-foreground">Admin Dashboard</h1>
            <p className="text-sm text-muted-foreground">Manage who can use TradeSync</p>
          </div>
          <Link to="/templates">
            <Button variant="outline" size="sm" className="min-h-9 rounded-lg text-xs font-medium">Templates</Button>
          </Link>
        </div>

        {/* The one time password, shown once */}
        {issued && (
          <Card className="p-4 bg-accent/5 border-accent/30 mb-5">
            <div className="flex items-start gap-2 mb-2">
              <KeyRound className="w-4 h-4 text-accent mt-0.5 shrink-0" />
              <div className="min-w-0">
                <h3 className="font-semibold text-foreground text-sm">{issued.label}</h3>
                <p className="text-xs text-muted-foreground">
                  Read this to {issued.email} now. It cannot be shown again, but you can issue another.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Input readOnly value={issued.password} className="h-10 font-mono md:text-sm bg-background" />
              <Button type="button" size="sm" variant="outline" onClick={() => copy(issued.password)} className="min-h-10 shrink-0">
                <Copy className="w-3.5 h-3.5" />
              </Button>
            </div>
            <Button variant="ghost" size="sm" onClick={() => setIssued(null)} className="mt-2 h-7 text-xs text-muted-foreground">
              Done, hide it
            </Button>
          </Card>
        )}

        {/* Add a rep */}
        <Button
          onClick={() => setAddDialogOpen(true)}
          size="sm"
          className="h-9 px-4 mb-5 text-xs font-bold bg-accent hover:bg-accent/90 text-accent-foreground"
        >
          <UserPlus className="w-3.5 h-3.5 mr-1.5" />Add RTF Rep
        </Button>

        {/* RTF Reps */}
        <Card className="p-4 bg-card">
          <div className="flex items-center gap-2 mb-3">
            <Users className="w-4 h-4 text-accent" />
            <h3 className="font-semibold text-foreground text-sm">RTF Reps ({users.length})</h3>
          </div>
          {isLoading ? (
            <div className="flex justify-center py-6"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
          ) : (
            <div className="space-y-2">
              {users.map(u => {
                const isSelf = u.id === user?.id;
                const busy = actionLoading === u.id;
                return (
                  <div key={u.id} className="p-3 rounded-lg bg-secondary/30">
                    <div className="min-w-0 mb-2">
                      <p className="text-sm font-medium text-foreground truncate">
                        {u.full_name}
                        {isSelf && <span className="text-xs text-muted-foreground"> (you)</span>}
                      </p>
                      <p className="text-xs text-muted-foreground truncate">{u.email}</p>
                      <div className="flex items-center gap-2 mt-1 flex-wrap">
                        <Badge variant="outline" className={`text-[10px] ${statusStyles[u.status] || statusStyles.active}`}>
                          {u.status === 'invited' ? 'invited, not yet activated' : u.status}
                        </Badge>
                        {u.role === 'admin' && <Badge className="text-[10px] bg-primary text-primary-foreground">admin</Badge>}
                        {u.status !== 'invited' && !u.has_password && (
                          <span className="inline-flex items-center gap-1 text-[10px] text-orange-600">
                            <AlertTriangle className="w-3 h-3" />no password yet
                          </span>
                        )}
                      </div>
                      {/* Same five fields, and the same missingProfileFields()
                          call, as the gate that blocks a rep from capturing
                          (worker/lib/profileCompleteness.ts, computed into
                          u.missing_profile_fields on every user response, task:
                          missing-field indicators, 2026-09-15). A complete
                          profile shows nothing extra here, on purpose, so this
                          row only gets busier for a rep who actually needs
                          attention.

                          Excludes a device account (bug fix, 2026-09-15): a
                          shared booth device will always show every one of
                          these five as missing, by design, there is no
                          personal job title, cell phone or headshot for it
                          to ever have, so the warning is never actionable
                          for that row and would otherwise permanently sit
                          there. u.missing_profile_fields itself stays raw
                          and unfiltered (worker/types.ts's toPublicUser),
                          same as the client gate's own bypass, this is
                          purely a display decision made here. */}
                      {!u.is_device_account && u.missing_profile_fields?.length > 0 && (
                        <p className="flex items-center gap-1 text-[10px] text-destructive mt-1">
                          <AlertTriangle className="w-3 h-3 shrink-0" />
                          Missing: {u.missing_profile_fields.map((f) => FIELD_LABELS[f] ?? f).join(', ')}
                        </p>
                      )}
                      {/* Task: show Gmail connection status on this list,
                          2026-09-16. u.google_connected/google_email already
                          ride on every user response (worker/types.ts's
                          toPublicUser), so this is a display-only addition,
                          no new endpoint. The reason this exists at all: an
                          unconnected rep's automated follow up emails do not
                          fail loudly, worker/lib/reminders.ts logs the
                          failure and quietly retries the same contact every
                          hour rather than surfacing it anywhere a person
                          would see it. This line is the only place an admin
                          would otherwise notice without asking each rep
                          directly.

                          Excludes a device account for the same reason the
                          missing-fields warning above does: a shared booth
                          device is never the sender of anything, every
                          contact it captures is attributed to a real rep
                          instead (CLAUDE.md section 15), so it will never
                          have, or need, its own Gmail connection. */}
                      {!u.is_device_account && (
                        <p className={`flex items-center gap-1 text-[10px] mt-1 ${u.google_connected ? 'text-muted-foreground' : 'text-destructive'}`}>
                          {u.google_connected
                            ? <><Mail className="w-3 h-3 shrink-0" />Gmail: sending as {u.google_email || 'their account'}</>
                            : <><MailX className="w-3 h-3 shrink-0" />Gmail: not connected</>}
                        </p>
                      )}
                    </div>

                    <div className="flex flex-wrap gap-1.5">
                      <Link to={`/admin/reps/${u.id}/edit`}>
                        <Button size="sm" variant="outline" disabled={busy} className="h-7 text-xs">
                          <Pencil className="w-3 h-3 mr-1" />Edit profile
                        </Button>
                      </Link>
                      {u.status === 'invited' ? (
                        <Button size="sm" variant="outline" disabled={resendingId === u.id} onClick={() => resendInvite(u)} className="h-7 text-xs">
                          {resendingId === u.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <><KeyRound className="w-3 h-3 mr-1" />Resend invite</>}
                        </Button>
                      ) : (
                        <Button size="sm" variant="outline" disabled={busy} onClick={() => act('reset', u)} className="h-7 text-xs">
                          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <><KeyRound className="w-3 h-3 mr-1" />New password</>}
                        </Button>
                      )}

                      {!isSelf && (
                        <>
                          {u.role === 'admin' ? (
                            <Button size="sm" variant="outline" disabled={busy} onClick={() => act('demote', u)} className="h-7 text-xs">
                              <ShieldOff className="w-3 h-3 mr-1" />Remove admin
                            </Button>
                          ) : (
                            <Button size="sm" variant="outline" disabled={busy} onClick={() => act('promote', u)} className="h-7 text-xs">
                              <Shield className="w-3 h-3 mr-1" />Make admin
                            </Button>
                          )}

                          {u.status === 'active' ? (
                            <Button size="sm" variant="outline" disabled={busy} onClick={() => act('suspend', u)} className="h-7 text-xs">
                              <Ban className="w-3 h-3 mr-1" />Suspend
                            </Button>
                          ) : (
                            <Button size="sm" variant="outline" disabled={busy} onClick={() => act('reactivate', u)} className="h-7 text-xs">
                              <Check className="w-3 h-3 mr-1" />
                              {u.status === 'invited' ? 'Activate' : 'Reactivate'}
                            </Button>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          <p className="text-[11px] text-muted-foreground mt-3">
            Accounts are suspended, never deleted. Contacts record who captured them, so
            removing a rep would erase who brought those leads in.
          </p>
        </Card>

        {/* ZoomInfo phone backfill, task: "Re-run ZoomInfo" admin tool
            (2026-09-24). Admin only, same as every other card on this page
            (the whole component returns early above for a non-admin), a real
            confirmation naming the count and every contact before anything
            spends a real ZoomInfo credit, the same pattern the bulk outbound
            send already uses for a real send. */}
        <Card className="p-4 bg-card mt-5">
          <div className="flex items-center gap-2 mb-3">
            <Phone className="w-4 h-4 text-accent" />
            <h3 className="font-semibold text-foreground text-sm">ZoomInfo Phone Backfill</h3>
          </div>
          <p className="text-xs text-muted-foreground mb-3">
            Real contacts caught by the directPhone incident (2026-09-24): a bad field name
            briefly broke ZoomInfo enrichment entirely for real captures at FS/TEC, not just
            phone. Re-runs a real lookup for each one, one real credit per contact, restoring
            email/title/company/LinkedIn suggestions, and now that mobilePhone is confirmed
            working, an actual phone number too. Internal team test captures are excluded
            automatically.
          </p>
          <Button
            type="button"
            onClick={handleRunBackfill}
            disabled={backfillBusy || !backfillData}
            size="sm"
            className="h-9 px-4 text-xs font-bold bg-accent hover:bg-accent/90 text-accent-foreground"
          >
            {backfillBusy ? (
              <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
            ) : (
              <Phone className="w-3.5 h-3.5 mr-1.5" />
            )}
            {backfillBusy
              ? 'Running...'
              : `Run backfill${backfillData ? ` (${backfillData.count})` : ''}`}
          </Button>

          {backfillResult?.ran && (
            <div className="mt-3 space-y-1.5">
              {backfillResult.outcomes.map((o) => (
                <div
                  key={o.contactId}
                  className="flex items-center justify-between gap-2 p-2 rounded-lg bg-secondary/30 text-xs"
                >
                  <span className="font-medium text-foreground truncate">{o.name}</span>
                  <span
                    className={`shrink-0 font-medium ${
                      o.outcome === 'phone_found'
                        ? 'text-emerald-600'
                        : o.outcome === 'no_phone'
                          ? 'text-muted-foreground'
                          : 'text-destructive'
                    }`}
                  >
                    {o.outcome === 'phone_found'
                      ? formatPhoneAsTyped(o.detail)
                      : o.outcome === 'no_phone'
                        ? 'No phone on file'
                        : 'Failed, safe to retry'}
                  </span>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Task: confirm 'mobilePhone' before it ever reaches a live
            contact (2026-09-24). Deliberately separate from the backfill
            card above: this never touches a real contact or the shared
            enrichment path either way, so it needs none of that card's
            candidate-count/confirmation-naming machinery, just a plain
            confirm before spending the one real credit. */}
        <Card className="p-4 bg-card mt-5">
          <div className="flex items-center gap-2 mb-3">
            <Phone className="w-4 h-4 text-accent" />
            <h3 className="font-semibold text-foreground text-sm">Test ZoomInfo mobilePhone Field</h3>
          </div>
          <p className="text-xs text-muted-foreground mb-3">
            One isolated call proving whether 'mobilePhone' is a valid, entitled field on this
            ZoomInfo plan, against Henry Schuck (ZoomInfo's own CEO, the same fixed test subject
            the auth diagnostic already uses), never a real contact. Does not touch live
            enrichment either way: a pass here is confirmation, not a live change. One real
            credit either way.
          </p>
          <Button
            type="button"
            onClick={handleTestMobilePhoneField}
            disabled={mobilePhoneTestBusy}
            size="sm"
            className="h-9 px-4 text-xs font-bold bg-accent hover:bg-accent/90 text-accent-foreground"
          >
            {mobilePhoneTestBusy ? (
              <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
            ) : (
              <Phone className="w-3.5 h-3.5 mr-1.5" />
            )}
            {mobilePhoneTestBusy ? 'Testing...' : 'Test mobilePhone field'}
          </Button>

          {mobilePhoneTestResult && (
            <div className="mt-3 space-y-2">
              <p className={`text-xs font-semibold ${mobilePhoneTestResult.ok ? 'text-emerald-600' : 'text-destructive'}`}>
                {mobilePhoneTestResult.ok
                  ? `ZoomInfo accepted the field (status ${mobilePhoneTestResult.status}).`
                  : `ZoomInfo rejected it (status ${mobilePhoneTestResult.status}).`}
              </p>
              <pre className="text-[10px] bg-secondary/30 rounded-lg p-2.5 overflow-x-auto whitespace-pre-wrap break-all">
                {JSON.stringify(mobilePhoneTestResult.response, null, 2)}
              </pre>
            </div>
          )}
        </Card>
        <div className="h-8" />
      </PageContainer>

      {/* Add a rep. Every field visible at once, no collapsed section: a
          required field (cell phone) hidden behind a reveal toggle is a
          required field that can silently go unfilled. */}
      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent className="max-w-sm max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-4 h-4 text-accent" />
              Add RTF Rep
            </DialogTitle>
            <DialogDescription>
              They get a one time password straight away. No invitation to accept.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-3">
            {/* Task: split user Name into First/Last, 2026-09-20. Replaces
                the old single Full name field; the server derives full_name
                from these two and keeps it in sync (worker/routes/users.ts),
                so every existing display location that reads full_name
                needed no change. */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-muted-foreground">
                  First name<span className="text-destructive"> *</span>
                </Label>
                <Input
                  value={newUser.first_name}
                  onChange={e => setNewUser(p => ({ ...p, first_name: e.target.value }))}
                  placeholder="Jane"
                  disabled={creating}
                  className="mt-1 h-10"
                />
                <p className="text-[10px] text-destructive mt-1">Required</p>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Last name</Label>
                <Input
                  value={newUser.last_name}
                  onChange={e => setNewUser(p => ({ ...p, last_name: e.target.value }))}
                  placeholder="Smith"
                  disabled={creating}
                  className="mt-1 h-10"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">
                Email<span className="text-destructive"> *</span>
              </Label>
              <Input
                type="email"
                value={newUser.email}
                onChange={e => setNewUser(p => ({ ...p, email: e.target.value }))}
                placeholder="jane@realtimefeedback.com"
                disabled={creating}
                className="mt-1 h-10"
              />
              <p className="text-[10px] text-destructive mt-1">Required</p>
            </div>
            {/* Task: admin-only "Shared Account" checkbox, 2026-09-16. Only
                on this create form, never on Profile.jsx or EditRep.jsx, so
                a regular rep has no way to reach or see this. Checking it
                hides the personal fields below entirely rather than just
                disabling them: none of them (title, phone, vCard, headshot,
                signature) will ever be used by a shared login, so showing
                them unusable would just be noise. A device account created
                this way is picked up automatically wherever is_device_account
                already matters (Profile.jsx, NewMeeting.jsx's capture
                screen, GET /api/reps' own exclusion from the "Scanning As"
                list), since none of that is keyed to this one account by
                name. */}
            <label className="flex items-center gap-2.5 py-1 cursor-pointer">
              <Checkbox
                checked={isDeviceAccount}
                onCheckedChange={(v) => setIsDeviceAccount(v === true)}
                disabled={creating}
              />
              <span className="text-sm text-foreground">Shared Account</span>
            </label>
            {isDeviceAccount ? (
              <p className="text-[11px] text-muted-foreground -mt-1.5">
                A shared login for a booth device, not a person. No job title, phone, vCard, headshot
                or signature, none of it would ever be used. It never appears as a choice in "Scanning
                As" on the capture screen; a real rep is picked there instead for every contact it
                captures.
              </p>
            ) : (
              // The "no calendar links" warning lives inside RepProfileFields' own
              // Calendar Links section now, right next to the actual list.
              <RepProfileFields value={newProfile} onChange={setNewProfile} disabled={creating} />
            )}
            <div className="flex gap-2 pt-1">
              <Button type="submit" disabled={creating} className="flex-1 min-h-10 bg-accent hover:bg-accent/90 text-accent-foreground">
                {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create account'}
              </Button>
              <Button type="button" variant="outline" disabled={creating} onClick={() => setAddDialogOpen(false)} className="min-h-10">
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Named by label, not by field key, same reasoning as Shows.jsx: a
          Create or Save button that silently does nothing is worse than one
          that says exactly what it needs. */}
      <Dialog open={missingFields.length > 0} onOpenChange={(open) => { if (!open) setMissingFields([]); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-destructive" />
              Missing required fields
            </DialogTitle>
            <DialogDescription>
              Fill these in before saving:
            </DialogDescription>
          </DialogHeader>
          <ul className="space-y-1.5">
            {missingFields.map((field) => (
              <li key={field.key} className="flex items-center gap-2 text-sm text-foreground">
                <span className="w-1.5 h-1.5 rounded-full bg-destructive shrink-0" />
                {field.label}
              </li>
            ))}
          </ul>
          <DialogFooter>
            <Button onClick={() => setMissingFields([])} className="w-full">Got it</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Mandatory bulk reassignment, task: contact reassignment (2026-09-08).
          Only appears when the rep being suspended still owns contacts; a
          rep who owns none suspends with no dialog at all. */}
      <Dialog open={!!reassignDialog} onOpenChange={(open) => { if (!open) setReassignDialog(null); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-amber-600" />
              Reassign before suspending
            </DialogTitle>
            <DialogDescription>
              {reassignDialog?.target?.full_name} still owns {reassignDialog?.contactCount} contact{reassignDialog?.contactCount === 1 ? '' : 's'}.
              Choose an active rep to move all of them to. This also stops their pending follow up reminders,
              since the new owner never met these people.
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label className="text-xs">Move contacts to</Label>
            <Select
              value={reassignDialog?.to || ''}
              onValueChange={(v) => setReassignDialog((prev) => ({ ...prev, to: v }))}
            >
              <SelectTrigger className="h-10 text-sm mt-1"><SelectValue placeholder="Choose a rep" /></SelectTrigger>
              <SelectContent>
                {users
                  .filter((u) => u.status === 'active' && u.id !== reassignDialog?.target?.id && !u.is_device_account)
                  .map((u) => (
                    <SelectItem key={u.id} value={u.id}>{u.full_name}</SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setReassignDialog(null)} disabled={reassigning} className="flex-1">
              Cancel
            </Button>
            <Button onClick={confirmReassignAndSuspend} disabled={!reassignDialog?.to || reassigning} className="flex-1">
              {reassigning ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Reassign and suspend'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
